import React, { useMemo } from "react";
import { cn } from "@/shared/lib/utils";
import { Button } from "@opal/components";
import Logo from "@/shared/ui/refresh-components/Logo";
import { SvgSidebar, SvgSearchMenu } from "@opal/icons";
import { useSettingsContext } from "@/app/providers/SettingsProvider";
import ChatSearchCommandMenu from "@/shared/ui/sections/sidebar/ChatSearchCommandMenu";

interface LogoSectionProps {
  folded?: boolean;
  onFoldClick?: () => void;
}

function LogoSection({ folded, onFoldClick }: LogoSectionProps) {
  const settings = useSettingsContext();
  const logoDisplayStyle = settings.enterpriseSettings?.logo_display_style;

  const logo = useMemo(
    () => (
      <div className="px-1">
        <Logo folded={folded} size={28} />
      </div>
    ),
    [folded]
  );
  const actionButtons = useMemo(
    () => (
      <div className="flex flex-row items-center gap-0.5 px-1 ml-auto">
        <ChatSearchCommandMenu
          trigger={
            <Button
              icon={SvgSearchMenu}
              prominence="tertiary"
              tooltip="Search"
              size="sm"
              className="text-text-03 hover:text-text-01 [&_svg]:stroke-[2px] opacity-80 hover:opacity-100"
            />
          }
        />
        <Button
          icon={SvgSidebar}
          prominence="tertiary"
          tooltip={folded ? "Open Sidebar" : "Close Sidebar"}
          size="sm"
          className="text-text-03 hover:text-text-01 [&_svg]:stroke-[2px] opacity-80 hover:opacity-100"
          onClick={onFoldClick}
        />
      </div>
    ),
    [folded, onFoldClick]
  );

  return (
    <div className="flex flex-row justify-between items-center pt-3 px-2">
      {folded === undefined ? (
        logo
      ) : folded && logoDisplayStyle !== "name_only" ? (
        <>
          <div className="group-hover/SidebarWrapper:hidden">{logo}</div>
          <div className="hidden group-hover/SidebarWrapper:flex">
            {actionButtons}
          </div>
        </>
      ) : folded ? (
        actionButtons
      ) : (
        <>
          {logo}
          {actionButtons}
        </>
      )}
    </div>
  );
}

export interface SidebarWrapperProps {
  folded?: boolean;
  onFoldClick?: () => void;
  children?: React.ReactNode;
}

export default function SidebarWrapper({
  folded,
  onFoldClick,
  children,
}: SidebarWrapperProps) {
  return (
    // This extra `div` wrapping needs to be present (for some reason).
    // Without, the widths of the sidebars don't properly get set to the explicitly declared widths (i.e., `4rem` folded and `15rem` unfolded).
    <div>
      <div
        className={cn(
          "h-screen flex flex-col bg-background-tint-02 py-2 gap-4 group/SidebarWrapper transition-all duration-200 ease-in-out",
          folded ? "w-0 opacity-0 overflow-hidden pointer-events-none" : "w-[16rem]"
        )}
      >
        <LogoSection folded={folded} onFoldClick={onFoldClick} />
        {children}
      </div>
    </div>
  );
}
